import type { MediaItem } from '@vv/common';
import type { ReactElement } from 'react';

import { CarouselDots, useCarouselPosition } from './CarouselDots';
import { YouTubeVideo } from './YouTubeVideo';

export interface MediaCarouselProps {
  items: MediaItem[];
  /** Tailwind width class for each slide; stands and pavilions differ. */
  slideClassName?: string;
}

/**
 * Scroll-snap rather than a JS carousel, so touch momentum, keyboard scrolling
 * and accessibility come from the browser. Dots reflect the scroll position and
 * scroll to the slide they stand for.
 */
export function MediaCarousel({
  items,
  slideClassName = 'w-[62%] max-w-[240px]',
}: MediaCarouselProps): ReactElement | null {
  const { trackRef, onScroll, active, goTo } = useCarouselPosition();

  if (items.length === 0) {
    return null;
  }

  return (
    <div>
      <div ref={trackRef} className="carousel" onScroll={onScroll}>
        {items.map((item) => (
          <figure
            key={item.id}
            className={`relative aspect-[4/5] overflow-hidden rounded-xl bg-ink-800 ${slideClassName}`}
          >
            {item.kind === 'video' ? (
              // A video's `url` is its YouTube id.
              <YouTubeVideo
                videoId={item.url}
                title={item.description}
                className="size-full"
                badge="corner"
              />
            ) : (
              <img
                src={item.thumbnailUrl ?? item.url}
                alt={item.description ?? ''}
                loading="lazy"
                className="size-full object-cover"
              />
            )}
          </figure>
        ))}
      </div>

      <CarouselDots count={items.length} active={active} onSelect={goTo} />
    </div>
  );
}
